/**
 * Gate two: is this connection fast enough to play on?
 *
 * N application-level round trips over the game socket, median compared
 * against `timings.json`. This measures the GAME path, and it works solo —
 * both properties are why it can gate entry, which a WebRTC measurement
 * cannot: there is no peer yet.
 *
 * It fails closed, with a polite exit rather than a spinner, because the
 * failure mode being avoided is a participant seated into a round they cannot
 * play and a partner whose session is wasted alongside theirs.
 */

import { useEffect, useRef, useState } from 'react'
import type { LatencyConfig } from '@overcooked/shared'
import type { LatencyResult, LatencyVerdict } from '../voice/latency'
import type { PingTransport } from '../voice/latency'
import type { LatencyGateState } from './latencyGateRun'
import { LatencyGateRun } from './latencyGateRun'

interface LatencyGateProps {
  transport: PingTransport
  config: LatencyConfig
  /**
   * Which gate of how many this is. Solo skips the mic check, so the gates are
   * numbered rather than hardcoded — "step 2 of 3" in front of a player who is
   * only ever shown two is a number they can count and catch us on.
   */
  step?: number
  of?: number
  onPass(result: LatencyResult): void
  onFail(verdict: LatencyVerdict, result: LatencyResult): void
}

const EXPLANATION: Record<Exclude<LatencyVerdict, 'pass'>, string> = {
  fail_slow: 'Your moves would show up late, which makes playing together hard.',
  fail_unstable: "Too many messages got lost, so some of your moves wouldn't go through.",
}

/** "Your delay is 312 ms. The limit is 250 ms." plus how many checks went unanswered. */
function delayLine(result: LatencyResult, limitMs: number): string {
  const delay = Number.isNaN(result.medianMs)
    ? "We couldn't measure your delay."
    : `Your delay is ${Math.round(result.medianMs)} ms. The limit is ${limitMs} ms.`
  return result.lost > 0 ? `${delay} ${result.lost} of ${result.requested} checks got no reply.` : delay
}

export function LatencyGate({ transport, config, step = 2, of = 3, onPass, onFail }: LatencyGateProps) {
  const [{ progress, outcome }, setState] = useState<LatencyGateState>({ progress: 0, outcome: null })
  const [attempt, setAttempt] = useState(0)

  // The run restarts on the transport, the thresholds or "Test again" — never
  // on the callbacks, whose identity a re-rendering parent changes freely
  // (`latencyGateRun.ts` says why that matters). StrictMode's extra mount
  // disposes the first run and starts a second, which costs a few pings.
  const run = useRef<LatencyGateRun | null>(null)
  if (!run.current) run.current = new LatencyGateRun(setState)
  useEffect(() => {
    run.current?.update(transport, config, attempt, { onPass, onFail })
  })
  useEffect(() => () => run.current?.dispose(), [])

  if (outcome) {
    const { verdict, result } = outcome
    return (
      <section className="gate gate-latency">
        <p className="gate-step">Step {step} of {of}</p>
        <h1>{verdict === 'fail_unstable' ? 'Your connection is unstable' : 'Your connection is too slow'}</h1>
        <p className="hint">
          {EXPLANATION[verdict as Exclude<LatencyVerdict, 'pass'>]} {delayLine(result, config.gateMaxMedianRttMs)}
        </p>
        <p className="hint">Try a cable instead of Wi-Fi, move closer to your router, or close any video calls.</p>
        <div className="gate-actions">
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Test again
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="gate gate-latency">
      <p className="gate-step">Step {step} of {of}</p>
      <h1>Checking your connection</h1>
      <p className="hint">This takes a few seconds.</p>
      <div className="meter" aria-label="connection check progress">
        <div className="meter-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
    </section>
  )
}
